import { getApiBaseUrl } from './config';
import { apiFetch } from './client';
const MASTER_TEMPLATE_NAME = "master";

/**
 * Checks the status of backend services (Flask, MySQL, Gemini API).
 * @returns {Promise<object>} Status data.
 */
export const getSystemStatus = async () => {
  try {
    const response = await apiFetch(`${getApiBaseUrl()}/status`, {}, { authCheck: false });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  } catch (error) {
    return {
      backend_status: 'Error',
      database_status: 'unreachable',
      gemini_key_status: 'missing',
      message: `NetworkError: Could not connect to backend: ${error.message}`,
      error: error.message
    };
  }
};

/**
 * Attempts to log in a user.
 * @param {string} username
 * @param {string} password
 * @returns {Promise<object>} User object with role and token.
 */
export const login = async (username, password) => {
  const res = await apiFetch(`${getApiBaseUrl()}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: username, password })
  }, { authCheck: false });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || 'Login failed');
  }
  return { id: json.id, name: json.name, role: json.role, email: json.email };
};

export const getCurrentSession = async () => {
  const res = await apiFetch(`${getApiBaseUrl()}/session`, {}, { authCheck: false });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(json.error || 'Failed to load session');
  return { id: json.id, name: json.name, role: json.role, email: json.email };
};

export const logout = async () => {
  const res = await apiFetch(`${getApiBaseUrl()}/logout`, { method: 'POST' }, { authCheck: false });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || 'Logout failed');
  return json;
};

export const requestPasswordReset = async (email) => {
  const res = await apiFetch(`${getApiBaseUrl()}/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  }, { authCheck: false });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || 'Failed to request password reset');
  }
  return json;
};

export const resetPasswordWithToken = async (token, password) => {
  const res = await apiFetch(`${getApiBaseUrl()}/reset-password/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password })
  }, { authCheck: false });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || 'Failed to reset password');
  }
  return json;
};

// --- Admin Template Management ---

/**
 * Fetch the current master template questions from backend.
 */
export const getMasterTemplate = async () => {
  const res = await apiFetch(`${getApiBaseUrl()}/templates/${encodeURIComponent(MASTER_TEMPLATE_NAME)}`);
  if (!res.ok) throw new Error("Failed to fetch master template");
  return res.json();
};

/**
 * Save or update the master template.
 */
export const saveMasterTemplate = async (templateJson) => {
  const res = await apiFetch(`${getApiBaseUrl()}/templates/${encodeURIComponent(MASTER_TEMPLATE_NAME)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ template: templateJson }),
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Failed to save master template: ${errText}`);
  }
  return true;
};

// --- Module Leader Template Management (UNIFIED) ---

/**
 * Fetches all modules managed by a specific module leader.
 * @param {number} leaderId - The account_id of the logged-in module leader.
 * @returns {Promise<Array>} List of modules {module_id, module_name, semester}.
 */
export const getLeaderModules = async (leaderId) => {
  const response = await apiFetch(`${getApiBaseUrl()}/leader/${leaderId}/modules`);
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Failed to fetch leader modules: ${errText}`);
  }
  return await response.json();
};

/**
 * Fetches all denied survey approval requests for a specific leader.
 * @param {number} leaderId - The account_id of the leader.
 * @returns {Promise<Array>} List of denied requests {request_id, module_id, module_name, status, feedback, actioned_at}.
 */
export const getLeaderDeniedRequests = async (leaderId) => {
  const response = await apiFetch(`${getApiBaseUrl()}/leader/${leaderId}/denied_requests`);
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Failed to fetch denied requests: ${errText}`);
  }
  return await response.json();
};


/**
 * Fetch the current module's extra template via the unified endpoint.
 * The identifier is now the module ID.
 * @param {number} moduleId - The ID of the module to get the template for.
 */
export const getLeaderTemplate = async (moduleId) => {
  const response = await apiFetch(`${getApiBaseUrl()}/templates/${moduleId}`); 
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Failed to fetch module template: ${errText}`);
  }
  return await response.json();
};

/**
 * Save updates to a module's extra template via the unified endpoint.
 * @param {number} moduleId - The ID of the module the template is linked to.
 * @param {Array<object>} template - The question data to save.
 * @param {number} currentUserId - The ID of the user saving the template (for question provenance).
 */
export const saveLeaderTemplate = async (moduleId, template, currentUserId) => {
  const response = await apiFetch(`${getApiBaseUrl()}/templates/${moduleId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ template, current_user_id: currentUserId }), 
  });
  
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Failed to save module template: ${errText}`);
  }
  return true; 
};


// --- Survey Approval Management ---

/**
 * Submits a survey configuration for administrator approval.
 * Note: Roster data is no longer sent here; it is pre-loaded by Admin via CSV.
 * @param {object} data - Contains module_id, leader_id, and adhoc_questions_text.
 */
export const submitSurveyForApproval = async (data) => {
  const response = await apiFetch(`${getApiBaseUrl()}/surveys/submit-for-approval`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });

    const jsonResponse = await response.json();

    if (!response.ok) {
        const errorMessage = jsonResponse.error || `Failed to submit survey. Status: ${response.status}`;
        throw new Error(errorMessage);
    }
    
    return jsonResponse;
};

/**
 * Fetches the question and heading count for a given template identifier.
 * @param {string} identifier - 'master', or 'module_{module_id}', or '{template_id}'
 * @returns {Promise<{question_count: number, heading_count: number}>}
 */
export const getTemplateQuestionCount = async (identifier) => {
  const res = await apiFetch(`${getApiBaseUrl()}/templates/count?identifier=${identifier}`);
  if (!res.ok) throw new Error("Failed to fetch template question count");
  return res.json();
};

/**
 * Resets a leader's password (returns the generated temporary password).
 * @param {number} leaderId
 */
export const resetLeaderPassword = async (leaderId) => {
  const response = await apiFetch(`${getApiBaseUrl()}/leader/${leaderId}/reset_password`, {
    method: 'POST'
  });
  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.error || 'Failed to reset password');
  }
  return json;
};

/**
 * Set a leader's password to a provided value.
 * @param {number} leaderId
 * @param {string} newPassword
 */
export const setLeaderPassword = async (leaderId, newPassword) => {
  const response = await apiFetch(`${getApiBaseUrl()}/leader/${leaderId}/set_password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password: newPassword })
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to set password');
  return json;
};

/**
 * Update a leader's email address.
 * @param {number} leaderId
 * @param {string} email
 */
export const updateLeaderEmail = async (leaderId, email) => {
  const response = await apiFetch(`${getApiBaseUrl()}/leader/${leaderId}/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  const json = await response.json();
  if (!response.ok) throw new Error(json.error || 'Failed to update email');
  return json;
};


/**
 * Fetches the combined survey questions and metadata for either a pending 
 * approval request or a deployed survey.
 * @param {object} params
 * @param {number} [params.requestId] - The ID of the SurveyApprovalRequests entry.
 * @param {number} [params.surveyId] - The ID of the Surveys entry.
 * @returns {Promise<object>} The combined survey view data.
 */
export const getViewSurveyQuestions = async ({ requestId, surveyId }) => {
    let url = `${getApiBaseUrl()}/templates/view_survey/`;

    if (requestId) {
        url += `?request_id=${requestId}`;
    } else if (surveyId) {
        url += `?survey_id=${surveyId}`;
    } else {
        throw new Error("Must provide either requestId or surveyId.");
    }

    try {
      const response = await apiFetch(url);
        if (!response.ok) {
            const errorBody = await response.json().catch(() => ({}));
            const errorMessage = errorBody.error || `Failed to fetch survey view. Status: ${response.status}`;
            throw new Error(errorMessage);
        }
        return await response.json();
    } catch (error) {
        console.error("API Error in getViewSurveyQuestions:", error);
        throw error;
    }
};

/**
 * Fetch survey access info by token.
 * @param {string} token
 */
export const fetchSurveyByToken = async (token) => {
  const res = await apiFetch(`${getApiBaseUrl()}/survey/access/${encodeURIComponent(token)}`, {}, { authCheck: false });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to fetch survey by token');
  return data;
};

/**
 * Submit survey answers via token.
 * @param {string} token
 * @param {Array} answers
 */
export const submitSurveyByToken = async (token, answers) => {
  const res = await apiFetch(`${getApiBaseUrl()}/survey/submit/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers })
  }, { authCheck: false });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Failed to submit survey');
  return data;
};

/**
 * Fetch aggregated module/survey results for a specific leader.
 * @param {number} leaderId
 */
export const getLeaderModuleResults = async (leaderId) => {
  const res = await apiFetch(`${getApiBaseUrl()}/leader/${leaderId}/module_results`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch module results');
  }
  return res.json();
};

/**
 * Fetch aggregated module/survey results for all modules (admin view).
 */
export const getAllModuleResults = async () => {
  const res = await apiFetch(`${getApiBaseUrl()}/admin/module_results`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch all module results');
  }
  return res.json();
};

/**
 * Fetch report details (stats + AI summary) for a specific survey.
 * @param {number} surveyId
 */
export const getSurveyReport = async (surveyId) => {
  const res = await apiFetch(`${getApiBaseUrl()}/surveys/${surveyId}/report`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to fetch survey report');
  }
  return data;
};

/**
 * Generate or regenerate the AI summary for a survey.
 * @param {number} surveyId
 * @param {boolean} force
 */
export const generateSurveySummary = async (surveyId, force = false) => {
  const res = await apiFetch(`${getApiBaseUrl()}/surveys/${surveyId}/summary`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ force })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Failed to generate summary');
  }
  return data;
};

/**
 * Download survey export in either PDF or XLSX format.
 * @param {number} surveyId
 * @param {'pdf'|'xlsx'|'csv'} format
 */
export const exportSurveyReport = async (surveyId, format = 'pdf') => {
  const exportFormat = ['pdf', 'xlsx', 'csv'].includes(format) ? format : 'pdf';
  const res = await apiFetch(`${getApiBaseUrl()}/surveys/${surveyId}/export?format=${exportFormat}`);

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to export ${exportFormat.toUpperCase()} report`);
  }

  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch ? filenameMatch[1] : `survey_${surveyId}_report.${exportFormat}`;

  return { blob, filename };
};